import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormLabel,
  IconButton,
  Input,
  Select,
  SimpleGrid,
  Spinner,
  Text,
  Textarea,
} from '@chakra-ui/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Plus, Trash } from 'phosphor-react';
import { useAuth } from '../../providers/auth';
import {
  IAtividadeSn,
  IParametroSn,
  IValorMensal,
  TELA_PATH,
  mensagemErro,
  snApi,
} from '../../pages/modulesHome/Supervisor/simplesProprio/api';
import { brl, cnpjFmt } from '../../pages/modulesHome/Supervisor/simplesProprio/regras';

const PADRAO: IParametroSn = {
  iss_fixo: false,
  valor_fixo_iss: null,
  icms_fora_do_das: false,
  anexo_padrao_servico: null,
  inicio_atividade: null,
  atividades: [],
  filiais: [],
  observacao: null,
};

export const chaveParametroSn = (empresaId?: number) => ['sn-parametro', empresaId];

/** Dados da aba (também decide se ela fica habilitada no modal). */
export function useParametroSn(empresaId?: number) {
  const { hasPermission } = useAuth();
  return useQuery({
    queryKey: chaveParametroSn(empresaId),
    queryFn: () => snApi.parametro(empresaId as number),
    enabled: !!empresaId && hasPermission(TELA_PATH, 'view'),
    retry: false,
  });
}

function Titulo({ children }: { children: React.ReactNode }) {
  return (
    <Text fontSize="xs" fontWeight="bold" color="purple.600" mb={3} textTransform="uppercase">
      {children}
    </Text>
  );
}

/** Lista de meses com valor (receita bruta anterior ou folha) editável. */
function ValoresMensais({
  titulo,
  ajuda,
  itens,
  comExterno,
  podeEditar,
  salvando,
  onSalvar,
}: {
  titulo: string;
  ajuda: string;
  itens: IValorMensal[];
  comExterno?: boolean;
  podeEditar: boolean;
  salvando: boolean;
  onSalvar: (v: IValorMensal[]) => void;
}) {
  const [novo, setNovo] = useState({ mes: '', valor: '', externo: '', obs: '' });
  return (
    <Box bg="white" borderRadius="lg" p={4} border="1px solid" borderColor="purple.100">
      <Titulo>{titulo}</Titulo>
      <Text fontSize="xs" color="gray.500" mb={2}>
        {ajuda}
      </Text>
      {itens.length === 0 && (
        <Text fontSize="sm" color="gray.400">
          Nenhum lançamento.
        </Text>
      )}
      {itens.map(i => (
        <Flex
          key={`${i.competencia}-${i.fonte ?? ''}`}
          justify="space-between"
          fontSize="sm"
          py={1}
        >
          <Text color="gray.600">
            {i.competencia.slice(5, 7)}/{i.competencia.slice(0, 4)}
            {i.fonte ? ` · ${i.fonte === 'declarada' ? 'declarada no PGDAS-D' : 'manual'}` : ''}
          </Text>
          <Text fontWeight="semibold">
            {brl(i.valor ?? i.valor_interno ?? 0)}
            {comExterno && i.valor_externo ? ` + ${brl(i.valor_externo)} ext.` : ''}
          </Text>
        </Flex>
      ))}
      {podeEditar && (
        <Flex gap={2} mt={3} wrap="wrap" align="flex-end">
          <FormControl w="150px">
            <FormLabel fontSize="xs" mb={1}>
              Mês
            </FormLabel>
            <Input
              size="sm"
              type="month"
              value={novo.mes}
              onChange={e => setNovo(v => ({ ...v, mes: e.target.value }))}
            />
          </FormControl>
          <FormControl w="140px">
            <FormLabel fontSize="xs" mb={1}>
              Valor
            </FormLabel>
            <Input
              size="sm"
              type="number"
              step="0.01"
              min={0}
              value={novo.valor}
              onChange={e => setNovo(v => ({ ...v, valor: e.target.value }))}
            />
          </FormControl>
          {comExterno ? (
            <FormControl w="140px">
              <FormLabel fontSize="xs" mb={1}>
                Mercado externo
              </FormLabel>
              <Input
                size="sm"
                type="number"
                step="0.01"
                min={0}
                value={novo.externo}
                onChange={e => setNovo(v => ({ ...v, externo: e.target.value }))}
              />
            </FormControl>
          ) : (
            <FormControl w="200px">
              <FormLabel fontSize="xs" mb={1}>
                Observação
              </FormLabel>
              <Input
                size="sm"
                value={novo.obs}
                onChange={e => setNovo(v => ({ ...v, obs: e.target.value }))}
              />
            </FormControl>
          )}
          <Button
            size="sm"
            colorScheme="purple"
            variant="outline"
            isDisabled={!novo.mes || novo.valor === ''}
            isLoading={salvando}
            onClick={() => {
              onSalvar([
                {
                  competencia: `${novo.mes}-01`,
                  valor: Number(novo.valor),
                  valor_externo: Number(novo.externo || 0),
                  observacao: novo.obs || null,
                },
              ]);
              setNovo({ mes: '', valor: '', externo: '', obs: '' });
            }}
          >
            Lançar
          </Button>
        </Flex>
      )}
    </Box>
  );
}

export function SimplesNacionalTab({ empresaId }: { empresaId: number }) {
  const qc = useQueryClient();
  const { hasPermission } = useAuth();
  const podeEditar = hasPermission(TELA_PATH, 'configurar');
  const { data, isLoading } = useParametroSn(empresaId);
  const [p, setP] = useState<IParametroSn>(PADRAO);
  const [filial, setFilial] = useState('');

  useEffect(() => {
    setP(data?.parametro ?? PADRAO);
  }, [data]);

  const { data: receitas = [] } = useQuery({
    queryKey: ['sn-receitas', empresaId],
    queryFn: () => snApi.receitas(empresaId),
  });
  const { data: folhas = [] } = useQuery({
    queryKey: ['sn-folhas', empresaId],
    queryFn: () => snApi.folhas(empresaId),
  });

  const salvar = useMutation({
    mutationFn: () => snApi.salvarParametro(empresaId, p),
    onSuccess: () => {
      toast.success('Parâmetros salvos: valem a partir do próximo cálculo.');
      qc.invalidateQueries({ queryKey: chaveParametroSn(empresaId) });
      qc.invalidateQueries({ queryKey: ['sn-lista'] });
    },
    onError: (e: any) => toast.error(mensagemErro(e)),
  });
  const salvarReceitas = useMutation({
    mutationFn: (v: IValorMensal[]) => snApi.salvarReceitas(empresaId, v),
    onSuccess: () => {
      toast.success('Receita lançada.');
      qc.invalidateQueries({ queryKey: ['sn-receitas', empresaId] });
    },
    onError: (e: any) => toast.error(mensagemErro(e)),
  });
  const salvarFolhas = useMutation({
    mutationFn: (v: IValorMensal[]) => snApi.salvarFolhas(empresaId, v),
    onSuccess: () => {
      toast.success('Folha lançada.');
      qc.invalidateQueries({ queryKey: ['sn-folhas', empresaId] });
    },
    onError: (e: any) => toast.error(mensagemErro(e)),
  });

  if (isLoading) {
    return (
      <Flex justify="center" py={10}>
        <Spinner color="purple.500" />
      </Flex>
    );
  }

  const set = (v: Partial<IParametroSn>) => setP(atual => ({ ...atual, ...v }));
  const setAtividade = (i: number, v: Partial<IAtividadeSn>) =>
    set({ atividades: p.atividades.map((a, j) => (j === i ? { ...a, ...v } : a)) });
  const filialDigitos = filial.replace(/\D/g, '');

  return (
    <Flex direction="column" gap={4}>
      <Box bg="white" borderRadius="lg" p={4} border="1px solid" borderColor="purple.100">
        <Titulo>Parâmetros da apuração</Titulo>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
          <Checkbox
            isChecked={p.iss_fixo}
            isDisabled={!podeEditar}
            onChange={e => set({ iss_fixo: e.target.checked })}
          >
            ISS fixo (recolhido fora do DAS)
          </Checkbox>
          <Checkbox
            isChecked={p.icms_fora_do_das}
            isDisabled={!podeEditar}
            onChange={e => set({ icms_fora_do_das: e.target.checked })}
          >
            ICMS fora do DAS (sublimite / benefício estadual)
          </Checkbox>
          {p.iss_fixo && (
            <FormControl>
              <FormLabel fontSize="xs" mb={1}>
                Valor fixo do ISS (opcional)
              </FormLabel>
              <Input
                size="sm"
                type="number"
                step="0.01"
                min={0}
                isDisabled={!podeEditar}
                value={p.valor_fixo_iss ?? ''}
                onChange={e =>
                  set({ valor_fixo_iss: e.target.value === '' ? null : Number(e.target.value) })
                }
              />
            </FormControl>
          )}
          <FormControl>
            <FormLabel fontSize="xs" mb={1}>
              Anexo padrão de serviço
            </FormLabel>
            <Select
              size="sm"
              isDisabled={!podeEditar}
              value={p.anexo_padrao_servico ?? ''}
              onChange={e =>
                set({
                  anexo_padrao_servico: e.target.value
                    ? (Number(e.target.value) as 3 | 4 | 5)
                    : null,
                })
              }
            >
              <option value="">Pelo item da LC 116</option>
              <option value="3">Anexo III</option>
              <option value="4">Anexo IV</option>
              <option value="5">Anexo V</option>
            </Select>
          </FormControl>
          <FormControl>
            <FormLabel fontSize="xs" mb={1}>
              Início de atividade (empresa nova: RBT12 proporcional)
            </FormLabel>
            <Input
              size="sm"
              type="date"
              isDisabled={!podeEditar}
              value={p.inicio_atividade ?? ''}
              onChange={e => set({ inicio_atividade: e.target.value || null })}
            />
          </FormControl>
        </SimpleGrid>

        <Text fontSize="xs" fontWeight="bold" color="gray.500" mt={4} mb={2}>
          REGRAS POR ITEM DA LC 116
        </Text>
        {p.atividades.map((a, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <Flex key={i} gap={2} mb={2} align="center" wrap="wrap">
            <Input
              size="sm"
              w="90px"
              placeholder="7.01"
              isDisabled={!podeEditar}
              value={a.item}
              onChange={e => setAtividade(i, { item: e.target.value })}
            />
            <Select
              size="sm"
              w="120px"
              isDisabled={!podeEditar}
              value={a.anexo}
              onChange={e => setAtividade(i, { anexo: Number(e.target.value) as 3 | 4 | 5 })}
            >
              <option value={3}>Anexo III</option>
              <option value={4}>Anexo IV</option>
              <option value={5}>Anexo V</option>
            </Select>
            <Checkbox
              isChecked={a.fator_r}
              isDisabled={!podeEditar}
              onChange={e => setAtividade(i, { fator_r: e.target.checked })}
            >
              Fator R
            </Checkbox>
            <Input
              size="sm"
              flex={1}
              minW="160px"
              placeholder="Nota (ex.: obra com material)"
              isDisabled={!podeEditar}
              value={a.nota ?? ''}
              onChange={e => setAtividade(i, { nota: e.target.value || null })}
            />
            {podeEditar && (
              <IconButton
                aria-label="Remover regra"
                size="sm"
                variant="ghost"
                colorScheme="red"
                icon={<Trash size={14} />}
                onClick={() => set({ atividades: p.atividades.filter((_, j) => j !== i) })}
              />
            )}
          </Flex>
        ))}
        {podeEditar && (
          <Button
            size="xs"
            variant="outline"
            leftIcon={<Plus size={12} />}
            onClick={() =>
              set({
                atividades: [...p.atividades, { item: '', anexo: 3, fator_r: false, nota: null }],
              })
            }
          >
            Adicionar regra
          </Button>
        )}

        <Text fontSize="xs" fontWeight="bold" color="gray.500" mt={4} mb={2}>
          FILIAIS (TODAS VÃO NA DECLARAÇÃO, MESMO SEM MOVIMENTO)
        </Text>
        {p.filiais.length === 0 && (
          <Text fontSize="sm" color="gray.400">
            Só a matriz.
          </Text>
        )}
        {p.filiais.map(c => (
          <Flex key={c} align="center" gap={2} fontSize="sm">
            <Text>{cnpjFmt(c)}</Text>
            {podeEditar && (
              <IconButton
                aria-label="Remover filial"
                size="xs"
                variant="ghost"
                colorScheme="red"
                icon={<Trash size={12} />}
                onClick={() => set({ filiais: p.filiais.filter(f => f !== c) })}
              />
            )}
          </Flex>
        ))}
        {podeEditar && (
          <Flex gap={2} mt={2}>
            <Input
              size="sm"
              w="220px"
              placeholder="CNPJ da filial"
              value={filial}
              onChange={e => setFilial(e.target.value)}
            />
            <Button
              size="sm"
              variant="outline"
              isDisabled={filialDigitos.length !== 14 || p.filiais.includes(filialDigitos)}
              onClick={() => {
                set({ filiais: [...p.filiais, filialDigitos] });
                setFilial('');
              }}
            >
              Adicionar
            </Button>
          </Flex>
        )}

        <FormControl mt={4}>
          <FormLabel fontSize="xs" mb={1}>
            Observação
          </FormLabel>
          <Textarea
            size="sm"
            isDisabled={!podeEditar}
            value={p.observacao ?? ''}
            onChange={e => set({ observacao: e.target.value || null })}
          />
        </FormControl>
        <Flex mt={3} align="center" gap={3}>
          {data?.parametro?.atualizado_por_nome && (
            <Text fontSize="xs" color="gray.500">
              Última alteração: {data.parametro.atualizado_por_nome}
            </Text>
          )}
          <Box flex={1} />
          {podeEditar && (
            <Button
              size="sm"
              colorScheme="purple"
              isLoading={salvar.isPending}
              isDisabled={p.atividades.some(a => !a.item.trim())}
              onClick={() => salvar.mutate()}
            >
              Salvar parâmetros
            </Button>
          )}
        </Flex>
      </Box>

      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4}>
        <ValoresMensais
          titulo="Receita bruta de meses anteriores"
          ajuda="Para cliente novo no escritório ou mês declarado por fora: entra no RBT12. Mês declarado pelo sistema aparece aqui sozinho."
          itens={receitas}
          comExterno
          podeEditar={podeEditar}
          salvando={salvarReceitas.isPending}
          onSalvar={v => salvarReceitas.mutate(v)}
        />
        <ValoresMensais
          titulo="Folha de salários (Fator R)"
          ajuda="Substitui a folha importada do Domínio no mês (pró-labore, salários, FGTS, INSS patronal)."
          itens={folhas}
          podeEditar={podeEditar}
          salvando={salvarFolhas.isPending}
          onSalvar={v => salvarFolhas.mutate(v)}
        />
      </SimpleGrid>
    </Flex>
  );
}
